import React, {useEffect, useState, useCallback} from 'react'
import ComponentConfig, {
  ComponentConfigProps,
  SaveButton,
  RemoveButton,
  Footer,
} from 'organization/Event/DashboardConfig/ComponentConfigPanel'
import {useForm} from 'react-hook-form'
import SettingsPanel from 'organization/Event/DashboardConfig/ComponentConfigPanel/SettingsPanel'
import StylingPanel from 'organization/Event/DashboardConfig/ComponentConfigPanel/StylingPanel'
import RulesPanel from 'organization/Event/DashboardConfig/ComponentConfigPanel/RulesPanel'
import BaseSettings from 'Event/Dashboard/components/NavButton/NavButtonConfig/BaseConfig/Settings'
import BaseStyling from 'Event/Dashboard/components/BlogPosts/Buttons/BlogPostButtonConfig/Styling/index'
import ScheduleInput from 'organization/Event/DashboardConfig/ComponentConfigPanel/ScheduleInput'
import {NavButtonWithSize} from 'Event/Dashboard/components/NavButton'
import {REMOVE, useSaveTemplate} from 'Event/TemplateUpdateProvider'
import {useAddTranslation, useRemoveTranslations} from 'Event/LanguageProvider'
import {replaceAtPath} from 'lib/JsonUpdateProvider'
import {generateHashId} from 'lib/crypto/hash'
import {useAutoUpdate} from 'organization/Event/TemplateEditor'
import get from 'lodash/get'

export default function BlogPostButtonConfig(
  props: ComponentConfigProps & {
    button: NavButtonWithSize
    id?: string | null
    postId: string
    onUpdate?: (id: string, button: NavButtonWithSize | typeof REMOVE) => void
    inputNamePrefix?: string
  },
) {
  const {
    showing: isVisible,
    onClose,
    id,
    button,
    postId,
    onUpdate,
    inputNamePrefix = `blogPosts.${postId}.buttons.${id}.`, // Add dot at the end of the prefix, otherwise it will create a undefined array
  } = props

  const {
    control,
    handleSubmit,
    watch,
    register,
    setValue,
    formState,
  } = useForm()

  const [rules, setRules] = useState(button.rules)
  const update = useSaveTemplate()
  const addTranslation = useAddTranslation()
  const removeTranslation = useRemoveTranslations()

  useAutoUpdate({
    values: watch(),
    when: isVisible,
  })

  useEffect(() => {
    if (isVisible) {
      return
    }

    setRules(button.rules)
  }, [isVisible, button])

  const set = (id: string, button: NavButtonWithSize) => {
    if (onUpdate) {
      onUpdate(id, button)
      return
    }

    const text = replaceAtPath(button, 'text', `{{${id}_text}}`)

    update({
      blogPosts: {
        [postId]: {
          buttons: {
            [id]: button,
          },
        },
      },
      localization: addTranslation({
        [`${id}_text`]: text ?? '',
      }),
    })
  }

  const removeButton = useCallback(() => {
    if (!id) {
      throw new Error('Missing button id')
    }

    if (onUpdate) {
      onUpdate(id, REMOVE)
      onClose()
      return
    }

    update({
      blogPosts: {
        [postId]: {
          buttons: {
            [id]: REMOVE,
          },
        },
      },
      localization: removeTranslation([`${id}_text`]),
    })

    onClose()
  }, [update, id, postId, onUpdate, removeTranslation, onClose])

  const submit = (buttonData: any) => {
    const inputNamePrefixWithoutDot = inputNamePrefix?.endsWith('.')
      ? inputNamePrefix.slice(0, -1)
      : inputNamePrefix

    const formData = inputNamePrefixWithoutDot
      ? get(buttonData, inputNamePrefixWithoutDot)
      : buttonData

    const data: NavButtonWithSize = {
      ...formData,
      rules,
    }

    if (props.id) {
      set(props.id, data)
    } else {
      generateHashId([
        'blogpost_',
        'button_',
        postId.toString(),
        Date.now().toString(),
        Math.random().toString(),
      ]).then((id) => {
        set(id, data)
      })
    }

    onClose()
  }

  return (
    <ComponentConfig
      showing={isVisible}
      onClose={onClose}
      title="Blog Post Button"
      onSubmit={handleSubmit(submit)}
      hasChanges={formState.isDirty}
    >
      <SettingsPanel>
        <BaseSettings
          control={control}
          register={register}
          watch={watch}
          button={button}
          setValue={setValue}
          id={props.id}
          inputNamePrefix={inputNamePrefix}
        />
      </SettingsPanel>
      <StylingPanel>
        <BaseStyling
          control={control}
          button={button}
          watch={watch}
          inputNamePrefix={inputNamePrefix}
        />
      </StylingPanel>
      <RulesPanel rules={rules} setRules={setRules}>
        <ScheduleInput
          control={control}
          setValue={setValue}
          values={button}
          inputNamePrefix={inputNamePrefix}
        />
      </RulesPanel>
      <Footer>
        <SaveButton type="submit" />
        <RemoveButton
          aria-label="remove button"
          onClick={removeButton}
          showing={Boolean(id)}
        />
      </Footer>
    </ComponentConfig>
  )
}
